// La lectura del Pulso en frases (Pau 08-10-2026; la de HOY se quitó el 10-10: «no me sirve», ahora son las ganancias
// esperadas, en Pulso.tsx). El servidor decide QUÉ decir (piezas con sus números,
// backend/src/sala/historia/lectura.ts y hoy.ts); aquí cada pieza se convierte en una frase con su plantilla (i18n),
// con las cifras que cambian en verde o en rojo. Sin IA: el mismo dato sale siempre con la misma frase.
import { Fragment, type ReactNode } from 'react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { diaConSemana, diaCorto, mesLargo, rotulo } from './fechas';
import { dinero, numero, valorMetrica } from './formato';
import type { CambioMetrica, ClaveHoy, PiezaRango, Tono } from './tipos';

const COLOR: Record<Tono, string> = { bien: 'text-oficina-trabajando', mal: 'text-oficina-error', neutro: 'text-oficina-texto' };
const PUNTO: Record<Tono, string> = { bien: 'bg-oficina-trabajando', mal: 'bg-oficina-error', neutro: 'bg-oficina-texto3' };

/** t() con huecos que pueden ser trozos de interfaz (una cifra coloreada). */
export function frase(clave: TKey, huecos: Record<string, ReactNode>): ReactNode {
  const plantilla = t(clave);
  const trozos = plantilla.split(/\{(\w+)\}/);
  return trozos.map((x, i) => <Fragment key={i}>{i % 2 ? (huecos[x] ?? `{${x}}`) : x}</Fragment>);
}

const Cifra = ({ tono, children }: { tono: Tono; children: ReactNode }) => <b className={cn('font-semibold', COLOR[tono])}>{children}</b>;

export function nombreSerie(clave: ClaveHoy | string): string {
  if (clave === 'ventas' || clave === 'fans' || clave === 'msgs') return t(`salaControl.series.${clave}` as TKey);
  return t(`salaControl.metricas.${clave}.nombre` as TKey);
}

function listaCambios(cambios: CambioMetrica[]): ReactNode {
  return cambios.map((c, i) => (
    <Fragment key={c.clave}>
      {i ? ' · ' : ''}
      {frase('salaControl.lectura.cambioItem', {
        nombre: nombreSerie(c.clave),
        de: valorMetrica(c.clave, c.a),
        a: <Cifra tono={c.tono}>{valorMetrica(c.clave, c.b)}</Cifra>,
      })}
    </Fragment>
  ));
}

const cuando = (desde: string, hasta: string) => (desde === hasta ? diaCorto(desde) : rotulo(desde, hasta));

function pctVentas(n: number | null): string {
  if (n === null) return '';
  if (Math.abs(n) < 2) return t('salaControl.lectura.ventas.igual');
  return t(n > 0 ? 'salaControl.lectura.ventas.mas' : 'salaControl.lectura.ventas.menos', { n: numero(Math.abs(n), Math.abs(n) < 10 ? 1 : 0) });
}

export function fraseRango(p: PiezaRango): ReactNode {
  switch (p.tipo) {
    case 'ventas': {
      const plantillaCambio: Record<string, TKey> = {
        anterior: 'salaControl.lectura.ventas.cambioAnterior',
        mes_anterior: 'salaControl.lectura.ventas.cambioMes',
        inicio_final: 'salaControl.lectura.ventas.cambioInicioFinal',
        dia: 'salaControl.lectura.ventas.cambioDia',
      };
      const cambio =
        p.cambioPct !== null && plantillaCambio[p.comparacion]
          ? frase(plantillaCambio[p.comparacion]!, { pct: <Cifra tono={p.tono}>{pctVentas(p.cambioPct)}</Cifra> })
          : '';
      return p.comparacion === 'dia'
        ? frase('salaControl.lectura.ventas.dia', { total: <b className="font-semibold">{dinero(p.total)}</b>, cambio })
        : frase('salaControl.lectura.ventas.rango', { total: <b className="font-semibold">{dinero(p.total)}</b>, media: <b className="font-semibold">{dinero(p.mediaDia)}</b>, cambio });
    }
    case 'fans':
      if (p.dias === 1) {
        return frase('salaControl.lectura.fansDia', {
          fans: <b className="font-semibold">{numero(p.distintos)}</b>,
          cambio: p.cambioPct !== null ? frase('salaControl.lectura.fansDiaCambio', { pct: <Cifra tono={p.tono}>{pctVentas(p.cambioPct)}</Cifra> }) : '',
        });
      }
      return frase('salaControl.lectura.fans', {
        distintos: <b className="font-semibold">{numero(p.distintos)}</b>,
        media: numero(p.mediaDia),
        cambio:
          p.cambioPct !== null && p.a !== null && p.b !== null
            ? frase('salaControl.lectura.fansCambio', { pct: <Cifra tono={p.tono}>{pctVentas(p.cambioPct)}</Cifra>, a: numero(p.a), b: numero(p.b) })
            : '',
      });
    case 'volumen':
      return frase('salaControl.lectura.volumen', { a: dinero(p.porFanA, 2), b: <Cifra tono="mal">{dinero(p.porFanB, 2)}</Cifra> });
    case 'mejoran':
      return frase('salaControl.lectura.mejoran', { lista: listaCambios(p.cambios) });
    case 'empeoran':
      return frase('salaControl.lectura.empeoran', { lista: listaCambios(p.cambios) });
    case 'momentos':
      return frase(p.cuantos === 1 ? 'salaControl.lectura.momento' : 'salaControl.lectura.momentos', {
        n: p.cuantos,
        cuando: <b className="font-semibold">{cuando(p.desde, p.hasta)}</b>,
        claves: p.claves.map(nombreSerie).join(', '),
      });
    case 'sin_momentos':
      return t('salaControl.lectura.sinMomentos');
    case 'faltan':
      return t('salaControl.lectura.faltan', { n: p.dias });
  }
}

/** Caja con la lectura en viñetas (punto verde / rojo / gris por frase). */
export function CajaLectura({ titulo, derecha, piezas }: { titulo: string; derecha?: string; piezas: Array<{ tono: Tono; texto: ReactNode }> }) {
  return (
    <div className="rounded-o-m bg-oficina-elevada px-5 py-4">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h3 className="text-o-nota font-semibold text-oficina-texto2">{titulo}</h3>
        {derecha ? <span className="text-o-nota text-oficina-texto3">{derecha}</span> : null}
      </div>
      <ul className="space-y-1.5">
        {piezas.map((p, i) => (
          <li key={i} className="flex gap-2.5 text-o-texto leading-snug text-oficina-texto2">
            <span className={cn('mt-[7px] h-2 w-2 shrink-0 rounded-full', PUNTO[p.tono])} aria-hidden />
            <span>{p.texto}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export { diaConSemana, mesLargo };
